// ====================================================================================
// Focus Trap Module - Vanilla JS
// ====================================================================================

/**
 * Creates a focus trap for a given container (like a dialog)
 * @param {HTMLElement} container - The container element to trap focus within
 * @returns {Object} - Object with enable/disable methods
 */
export const createFocusTrap = (container) => {
  let isActive = false;

  const getFocusableElements = () => {
    const focusableSelectors = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ];
    if (focusableSelectors.length === 0) {
      console.warn("No focusable selectors defined for focus trap.");
    }

    const elements = container.querySelectorAll(focusableSelectors.join(", "));

    return Array.from(elements).filter((el) => {
      const style = window.getComputedStyle(el);
      return (
        el.offsetParent !== null &&
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        parseFloat(style.opacity) > 0
      );
    });
  };

  const handleTabKey = (e) => {
    if (!isActive || e.key !== "Tab") return;

    const focusableElements = Array.from(getFocusableElements()).filter(
      (el) => el.offsetParent !== null || el.checkVisibility(), // Only visible elements
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab (backwards)
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab (forwards)
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  const enable = () => {
    if (isActive) return;

    isActive = true;
    document.addEventListener("keydown", handleTabKey);

    // Set initial focus to first focusable element
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  };

  const disable = () => {
    if (!isActive) return;

    isActive = false;
    document.removeEventListener("keydown", handleTabKey);
  };

  return {
    enable,
    disable,
    isActive: () => isActive,
  };
};

/**
 * Simple helper to create and manage focus trap for dialogs
 * @param {string} dialogSelector - CSS selector for the dialog element
 * @returns {Object} - Object with methods to control the focus trap
 */
export const createDialogFocusTrap = (dialogSelector) => {
  let dialog;
  if (typeof dialogSelector == "string") {
    dialog = document.querySelector(dialogSelector);
  } else if (dialogSelector instanceof HTMLElement) {
    dialog = dialogSelector;
  } else {
    console.error(
      "Invalid dialogSelector provided to createDialogFocusTrap. Should be either a string or an HTMLElement.",
      dialogSelector,
    );
  }
  if (!dialog) {
    console.warn(`Dialog not found: ${dialogSelector}`);
    return { enable: () => {}, disable: () => {} };
  }
  const focusTrap = createFocusTrap(dialog);
  const isPopover = dialog.hasAttribute("popover");
  let observer = null;

  if (isPopover) {
    // Use toggle event for popovers
    dialog.addEventListener("toggle", (e) => {
      if (e.newState === "open") {
        focusTrap.enable();
      } else {
        focusTrap.disable();
      }
    });
  } else {
    // Auto-enable when dialog opens
    observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const isOpenAttribute = dialog.hasAttribute("open");
        const isOpenClass = dialog.classList.contains("open");
        const isOpen = isOpenAttribute || isOpenClass;
        if (
          mutation.attributeName === "open" ||
          mutation.attributeName === "class"
        ) {
          if (isOpen) {
            focusTrap.enable();
          } else {
            focusTrap.disable();
          }
        }
      });
    });

    observer.observe(dialog, { attributes: true });
  }

  return {
    enable: focusTrap.enable,
    disable: focusTrap.disable,
    isActive: focusTrap.isActive,
    destroy: () => {
      focusTrap.disable();
      if (observer) {
        observer.disconnect();
      }
    },
  };
};
